import { Mail } from "lucide-react";
import { useMemo, useState } from "react";

export default function Newsletter() {
  const queryResult = useMemo(() => new URLSearchParams(window.location.search).get("newsletter"), []);
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState(queryResult === "confirmed" || queryResult === "unsubscribed" ? "success" : queryResult === "invalid" ? "error" : "idle");
  const [message, setMessage] = useState(resultMessage(queryResult));

  const subscribe = async (event) => {
    event.preventDefault();
    setStatus("loading");
    setMessage("Sending your confirmation email…");
    const form = new FormData(event.currentTarget);
    try {
      const response = await fetch("/api/newsletter/subscribe", {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({ email, consent, website: String(form.get("website") || "") }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || "The subscription request could not be completed.");
      setStatus("success");
      setMessage("Check your inbox and confirm your subscription. The letter will not start until you do.");
    } catch (error) {
      setStatus("error");
      setMessage(error.message || "The subscription request could not be completed. Try again in a moment.");
    }
  };

  return (
    <section className="newsletter-invite" id="newsletter" aria-labelledby="newsletter-title">
      <div className="newsletter-invite__heading">
        <Mail size={28} strokeWidth={1.5} aria-hidden="true" />
        <div>
          <h2 id="newsletter-title">A note, when I have one.</h2>
          <p>I send occasional mental-health notes for patients, families, and professional peers. Each letter includes one subject and a practical way to think about it.</p>
        </div>
      </div>
      <form className="newsletter-form" onSubmit={subscribe} data-state={status}>
        <div className="newsletter-form__field">
          <label htmlFor="newsletter-email">Email address</label>
          <input
            id="newsletter-email"
            name="email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            required
            aria-invalid={status === "error"}
            aria-describedby="newsletter-message"
          />
          <div className="newsletter-honeypot" aria-hidden="true" inert="">
            <label htmlFor="newsletter-website">Website</label>
            <input id="newsletter-website" name="website" tabIndex={-1} autoComplete="off" />
          </div>
        </div>
        <label className="newsletter-consent">
          <input type="checkbox" checked={consent} onChange={(event) => setConsent(event.target.checked)} required />
          <span>I agree to receive Neerja Notes by email. Letters use basic open and click tracking, and I can unsubscribe from any letter.</span>
        </label>
        <div className="newsletter-form__action">
          <button className="button button--ink" type="submit" disabled={status === "loading"}>
            {status === "loading" ? "Sending…" : "Subscribe"}
          </button>
          <p id="newsletter-message" className={`form-message form-message--${status}`} aria-live="polite">
            {message || "Your address is used only for these letters."}
          </p>
        </div>
      </form>
      <p className="newsletter-invite__contact">Prefer email? Write to <a href="mailto:hi@neerjagidwani.com">hi@neerjagidwani.com</a>.</p>
    </section>
  );
}

function resultMessage(result) {
  if (result === "confirmed") return "You’re subscribed. Your next letter will arrive by email.";
  if (result === "unsubscribed") return "You’ve been unsubscribed. You can subscribe again whenever you wish.";
  if (result === "invalid") return "That newsletter link has expired or was already used. Request a fresh confirmation below.";
  return "";
}
